import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Sun, Snowflake, ArrowRight } from "lucide-react";
import summerScene from "@/assets/summer-alpine-scene.jpg";
import winterScene from "@/assets/winter-alpine-scene.jpg";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/data/translations";

// Activity images
import lakeThunWatersports from "@/assets/lake-thun-watersports.jpg";
import alpineHikingTrails from "@/assets/alpine-hiking-trails.jpg";
import botanicalGarden from "@/assets/botanical-garden-alpine.jpg";
import outdoorYoga from "@/assets/outdoor-yoga-mountains.jpg";
import skiShuttle from "@/assets/ski-shuttle-service.jpg";
import crossCountrySkiing from "@/assets/cross-country-skiing.jpg";
import thermalSpa from "@/assets/thermal-spa-winter.jpg";
import snowshoeing from "@/assets/snowshoeing-adventure.jpg";

const SeasonalGuide = () => {
  const { language } = useLanguage();
  const [activeSeason, setActiveSeason] = useState<"summer" | "winter">(
    "summer",
  );

  const seasons = useMemo(() => {
    const seasonalData = translations[language].localAttractions.seasonal;

    return {
      summer: {
        title: seasonalData.alpineHiking,
        background: summerScene,
        activities: [
          {
            title: seasonalData.lakeWaterSports,
            description: seasonalData.lakeWaterSportsDesc,
            image: lakeThunWatersports,
          },
          {
            title: seasonalData.alpineHiking,
            description: seasonalData.alpineHikingDesc,
            image: alpineHikingTrails,
          },
          {
            title: seasonalData.photography,
            description: seasonalData.photographyDesc,
            image: botanicalGarden,
          },
          {
            title: seasonalData.culinary,
            description: seasonalData.culinaryDesc,
            image: outdoorYoga,
          },
        ],
      },
      winter: {
        title: seasonalData.skiSnowboard,
        background: winterScene,
        activities: [
          {
            title: seasonalData.skiSnowboard,
            description: seasonalData.skiSnowboardDesc,
            image: skiShuttle,
          },
          {
            title: seasonalData.mountainAdventures,
            description: seasonalData.mountainAdventuresDesc,
            image: crossCountrySkiing,
          },
          {
            title: seasonalData.photography,
            description: seasonalData.photographyDesc,
            image: thermalSpa,
          },
          {
            title: seasonalData.mountainAdventures,
            description: seasonalData.mountainAdventuresDesc,
            image: snowshoeing,
          },
        ],
      },
    };
  }, [language]);

  const currentSeason = seasons[activeSeason];

  return (
    <section
      id="dining"
      className="relative py-32 min-h-[120vh] flex items-center"
      style={{
        backgroundImage: `url(${currentSeason.background})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
      data-oid="w5wl6qf"
    >
      {/* White gradient overlay at top */}
      <div
        className="absolute top-0 left-0 right-0 h-[100px] bg-gradient-to-b from-white to-transparent"
        data-oid=":2v80ji"
      ></div>

      {/* Minimal overlay only at bottom for text readability */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-transparent 85% to-pine-green/30"
        data-oid="nf7k99k"
      ></div>

      {/* Asymmetric curved bottom border */}
      <div
        className="absolute bottom-0 left-0 w-full overflow-hidden"
        data-oid="uwy.yg2"
      >
        <svg
          className="relative block w-full h-[100px]"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          data-oid="tgxnj38"
        >
          <path
            d="M0,40 C300,80 600,20 900,60 C1050,80 1150,40 1200,20 L1200,120 L0,120 Z"
            fill="#F6F2EC"
            data-oid="ae1544r"
          />
        </svg>
      </div>

      <div
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full"
        data-oid="59:9dxh"
      >
        {/* Season Toggle */}
        <div className="text-center mb-16" data-oid=":06l2h0">
          <h2
            className="font-playfair text-4xl md:text-5xl font-bold text-elegant-white mb-8 uppercase"
            data-oid="wfe.03z"
          >
            {currentSeason.title}
          </h2>

          <div
            className="inline-flex bg-elegant-white/20 backdrop-blur-md rounded-full p-2"
            data-oid="4xn.frf"
          >
            <button
              onClick={() => setActiveSeason("summer")}
              className={`flex items-center space-x-2 px-6 py-3 rounded-full transition-all duration-300 ${
                activeSeason === "summer"
                  ? "bg-elegant-white text-pine-green shadow-lg"
                  : "text-elegant-white hover:bg-elegant-white/10"
              }`}
              data-oid="ewr4ab1"
            >
              <Sun size={20} data-oid="6w:onl8" />
              <span className="font-lato font-medium" data-oid="u8hm.p1">
                {translations[language].localAttractions.seasonal.summer}
              </span>
            </button>
            <button
              onClick={() => setActiveSeason("winter")}
              className={`flex items-center space-x-2 px-6 py-3 rounded-full transition-all duration-300 ${
                activeSeason === "winter"
                  ? "bg-elegant-white text-pine-green shadow-lg"
                  : "text-elegant-white hover:bg-elegant-white/10"
              }`}
              data-oid="v_h0wn_"
            >
              <Snowflake size={20} data-oid="h30slic" />
              <span className="font-lato font-medium" data-oid="sn-ei.f">
                {translations[language].localAttractions.seasonal.winter}
              </span>
            </button>
          </div>
        </div>

        {/* Activity Cards */}
        <div
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          data-oid="uy0iul8"
        >
          {currentSeason.activities.map((activity, index) => (
            <div
              key={`${activeSeason}-${index}`}
              className="relative bg-elegant-white/10 backdrop-blur-md rounded-lg p-6 hover:bg-elegant-white/20 transition-all duration-300 hover:scale-105 hover:shadow-elegant group min-h-[320px] flex flex-col overflow-hidden"
              data-oid="0h7go:u"
            >
              {/* Background image on hover only */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  backgroundImage: `url(${activity.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
                data-oid="zz.0yxf"
              ></div>

              {/* Gradient overlay on hover only */}
              <div
                className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                data-oid="r7:wth0"
              ></div>

              {/* Content */}
              <div
                className="relative z-10 flex flex-col h-full"
                data-oid="2kzuxxk"
              >
                <h3
                  className="font-playfair text-xl font-semibold text-elegant-white mb-3 leading-tight"
                  data-oid="7f9j7rn"
                >
                  {activity.title}
                </h3>
                <p
                  className="font-lato text-elegant-white/80 mb-4 leading-relaxed flex-1 text-sm"
                  data-oid="hc5.8qs"
                >
                  {activity.description}
                </p>
                <div className="mt-auto pt-2" data-oid="9l3yqws">
                  <Button
                    variant="elegant"
                    size="sm"
                    className="bg-elegant-white/20 border-elegant-white/30 text-elegant-white hover:bg-elegant-white hover:text-pine-green w-full"
                    data-oid="xkkd9js"
                  >
                    {
                      translations[language].localAttractions.quickAccess
                        .learnMore
                    }
                    <ArrowRight
                      size={14}
                      className="ml-1 group-hover:translate-x-1 transition-transform duration-300"
                      data-oid="g11rari"
                    />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12" data-oid="auktq.8">
          <Button
            asChild
            size="lg"
            className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-4 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            trackingType="booking"
            trackingLabel={
              translations[language].localAttractions.seasonal.bookYourStay
            }
            trackingLocation="Seasonal Guide Section"
            data-oid="e05h_hg"
          >
            <a
              href={import.meta.env.VITE_BOOKING_URL}
              data-oid="qm-776."
            >
              {translations[language].localAttractions.seasonal.bookYourStay}
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SeasonalGuide;
